import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useBeforeUnload, useBlocker, useLocation } from "react-router-dom";
import { MenuHeader } from "../pages/Dashboard/block/MenuHeader/MenuHeader";
import { MenuIcon } from "../pages/Dashboard/MenuIcon";
import { useMenuScroll } from "../pages/shared/useMenuScroll";
import { AppShellContext } from "./AppShellContext";
import { useNativeMessengerNavigation } from "../api/useNativeMessengerNavigation";
import { Navigation } from "./Navigation";

function useDesktop() {
    const [desktop, setDesktop] = useState(() => window.matchMedia("(min-width: 1100px)").matches);
    useEffect(() => {
        const media = window.matchMedia("(min-width: 1100px)");
        const sync = () => setDesktop(media.matches);
        media.addEventListener("change", sync);
        return () => media.removeEventListener("change", sync);
    }, []);
    return desktop;
}

export default function AppShell() {
    const [menu, setMenu] = useState(false);
    const openMenu = useCallback(() => setMenu(true), []);
    const messenger = useNativeMessengerNavigation(openMenu);
    const [drafts, setDrafts] = useState<Record<string, string>>({});
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
    const desktop = useDesktop();
    const location = useLocation();
    const assistantChat = location.pathname === "/assistant" && new URLSearchParams(location.search).get("mode") !== "tools";
    const conversation = /^\/(chats|support\/ticket|order-layout\/ticket)/.test(location.pathname) || assistantChat;
    const listRef = useRef<HTMLUListElement>(null);
    const sidebarRef = useRef<HTMLElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);
    const appRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLElement>(null);
    useEffect(() => {
        const app = appRef.current; const header = headerRef.current;
        if (!app || !header) return;
        const viewport = window.visualViewport;
        const sync = () => {
            app.style.setProperty("--lkfr-header-height", `${header.getBoundingClientRect().height}px`);
            app.style.setProperty("--lkfr-viewport-height", `${viewport?.height ?? window.innerHeight}px`);
            app.dataset.keyboardOpen = String((viewport?.height ?? window.innerHeight) < window.innerHeight - 120);
        };
        const observer = new ResizeObserver(sync); observer.observe(header);
        window.addEventListener("resize", sync); viewport?.addEventListener("resize", sync); sync();
        return () => { observer.disconnect(); window.removeEventListener("resize", sync); viewport?.removeEventListener("resize", sync); };
    }, []);
    const hasDrafts = Object.keys(drafts).length > 0;
    const blocker = useBlocker(({ currentLocation, nextLocation, historyAction }) => {
        // Saving the current chat ID updates its URL without leaving the editor.
        const chatUrlUpdate = historyAction === "REPLACE" && currentLocation.pathname === "/assistant" && nextLocation.pathname === "/assistant"
            && new URLSearchParams(currentLocation.search).get("mode") !== "tools" && new URLSearchParams(nextLocation.search).get("mode") !== "tools";
        return hasDrafts && !chatUrlUpdate && (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search);
    });
    useMenuScroll(menu && !desktop, listRef);
    useBeforeUnload(useCallback((event: BeforeUnloadEvent) => {
        if (hasDrafts) { event.preventDefault(); event.returnValue = ""; }
    }, [hasDrafts]));
    const registerDraft = useCallback((id: string, label: string | null) => {
        setDrafts(previous => {
            if ((previous[id] || null) === label) return previous;
            const next = { ...previous };
            if (label) next[id] = label; else delete next[id];
            return next;
        });
    }, []);
    const runGuarded = useCallback((action: () => void) => {
        if (hasDrafts) setPendingAction(() => action); else action();
    }, [hasDrafts]);
    const context = useMemo(() => ({ registerDraft, runGuarded }), [registerDraft, runGuarded]);
    useEffect(() => {
        setMenu(false);
        if (!conversation) window.scrollTo({ top: 0, behavior: "auto" });
    }, [location.pathname, conversation]);
    useEffect(() => { if (desktop) setMenu(false); }, [desktop]);
    useEffect(() => { if (sidebarRef.current) sidebarRef.current.inert = !desktop && !menu; }, [desktop, menu]);
    const showConfirmation = blocker.state === "blocked" || pendingAction !== null;
    const closeConfirmation = useCallback(() => {
        if (blocker.state === "blocked") blocker.reset();
        setPendingAction(null);
    }, [blocker]);
    useEffect(() => {
        const handleKey = (event: KeyboardEvent) => {
            if (event.key === "Escape") { if (showConfirmation) closeConfirmation(); else setMenu(false); }
            if (event.key === "Tab" && (showConfirmation || (menu && !desktop))) {
                const container = showConfirmation ? dialogRef.current : sidebarRef.current;
                const controls = Array.from(container?.querySelectorAll<HTMLElement>('a[href],button:not([disabled]),input,select,textarea,[tabindex="0"]') || []);
                const first = controls[0], last = controls[controls.length - 1];
                if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
                else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
            }
        };
        document.addEventListener("keydown", handleKey);
        if (showConfirmation) dialogRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
        else if (menu && !desktop) sidebarRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
        return () => document.removeEventListener("keydown", handleKey);
    }, [menu, desktop, showConfirmation, closeConfirmation]);
    return <AppShellContext.Provider value={context}>
        <div ref={appRef} className={`lkfr-app${conversation ? " lkfr-app--conversation" : ""}${assistantChat ? " lkfr-app--assistant" : ""}`}>
            {menu && !desktop && <button className="lkfr-nav-backdrop" aria-label="Закрыть меню" tabIndex={-1} onClick={() => setMenu(false)} />}
            <aside ref={sidebarRef} className={`lkfr-sidebar${menu ? " open" : ""}`} aria-hidden={!desktop && !menu}>
                <button className="lkfr-nav-close" aria-label="Закрыть меню" onClick={() => setMenu(false)}>×</button>
                <Navigation listRef={listRef} onNavigate={() => setMenu(false)} />
            </aside>
            <div className="lkfr-workspace">
                <header ref={headerRef} className="lkfr-shell-header"><MenuHeader standalone onMenuClick={() => setMenu(true)} /></header>
                <main id="lkfr-main" className="lkfr-content"><Outlet /></main>
            </div>
            {!conversation && <nav className="lkfr-bottom-nav" aria-label="Быстрые разделы">
                <NavLink to="/" end className={({ isActive }) => isActive ? "active" : ""}><MenuIcon name="home" /><span>Главная</span></NavLink>
                <NavLink to="/records" className={({ isActive }) => isActive ? "active" : ""}><MenuIcon name="calendar" /><span>Записи</span></NavLink>
                {messenger.enabled ? <button onClick={messenger.open} aria-label="Общение"><span className="lkfr-messenger-icon"><MenuIcon name="chat" />{messenger.unreadCount > 0 ? <span className="lkfr-messenger-badge">{messenger.unreadLabel}</span> : null}</span><span>Общение</span></button> : <NavLink to="/operations-day" className={({ isActive }) => isActive ? "active" : ""}><MenuIcon name="clock" /><span>Сегодня</span></NavLink>}
                <NavLink to="/support" className={({ isActive }) => isActive ? "active" : ""}><MenuIcon name="tools" /><span>Помощь</span></NavLink>
                <button onClick={() => setMenu(true)} aria-label="Все разделы"><span className="lkfr-more-icon" aria-hidden="true">···</span><span>Ещё</span></button>
            </nav>}
        </div>
        {showConfirmation && <div className="lkfr-dialog-overlay"><div ref={dialogRef} className="lkfr-dialog" role="dialog" aria-modal="true" aria-labelledby="draft-dialog-title">
            <span className="lkfr-eyebrow">Несохранённые изменения</span><h2 id="draft-dialog-title">Продолжить редактирование?</h2>
            <p>В форме остались изменения. При переходе они будут потеряны.</p><ul>{Array.from(new Set(Object.values(drafts))).map(label => <li key={label}>{label}</li>)}</ul>
            <div className="lkfr-dialog-actions"><button className="business-primary" onClick={closeConfirmation}>Остаться</button><button className="business-secondary" onClick={() => {
                const action = pendingAction; setPendingAction(null);
                if (blocker.state === "blocked") blocker.proceed();
                else action?.();
            }}>Перейти без сохранения</button></div>
        </div></div>}
    </AppShellContext.Provider>;
}
